import * as React from 'react';

import * as UI from 'core/ui';
import * as Images from 'pos/Images';

import { BackActionButton, Content, Footer, Header, LargeDeviceOnly, PhoneOnly, PosScreen } from 'pos/ui';
import { Result } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Command } from 'pos/common';
import { Res, Permission, RecentSaleDTO, BuyerCustomerDTO } from 'core/api';
import { Currency } from 'core/utils';
import { CustomerFacade } from 'pos/facades';
import { Message, Modal } from 'core/ui-next/modal';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { HStack, VStack, Text, Table, Box } from '@chakra-ui/react';
import { SvgIcon } from 'core/ui-next/chakra-components';
import { ChakraUtils } from 'core/ui-next';
import { z } from 'zod';
import { Close } from 'core/img';
import { CheckBoxMenuItemDef } from 'core/ui-next/controls/CheckBoxMenu';

type Props = { customerFiscalName: string, recentSales: RecentSaleDTO[] } & Modal.Actions<void>;


// Identificadores de todas las columnas visibles de la tabla en la vista Phone.
// Se usa para saber qué columnas se muestran/ocultan según los checks del menú.
type ColumnKey = 'FamilyName' | 'SaleFormatName' | 'Quantity' | 'Price' | 'CashDiscount' | 'DiscountRate' | 'TotalAmount' | 'BusinessDay' | 'UserName';
const ALL_COLUMN_KEYS: ColumnKey[] = ['FamilyName', 'SaleFormatName', 'Quantity', 'Price', 'CashDiscount', 'DiscountRate', 'TotalAmount', 'BusinessDay', 'UserName'];


const stateSchema = z.object({
	criteria: z.string()
});

type FormState = z.infer<typeof stateSchema>;


function ShowRecentSalesByCustomerScreen(props: Props) {

	const { customerFiscalName, recentSales, cancel } = props;

	const formRef = React.useRef<FormRef<FormState>>(null);
	const { Form, Input, CheckBoxMenu } = useControlsFor<FormState>();


	const initialState: FormState = ({
		criteria: ''
	});


	const formatProduct = (_saleFormatName: string, s: RecentSaleDTO) => {
		const productName = s.SizeId !== 0 ? `${s.SaleFormatName} ${s.SizeName}\\${s.ColorName}` : s.SaleFormatName;
		return (s.MenuTag === '' ? '' : `(${s.MenuTag}) `) + productName + s.Addins.reduce((prev, curr) => { return prev + ' ' + Res.Web.TICKET_LINES_CONTROL_PRODUCT_WITH_LBL(curr); }, '');
	};

	const onCancel = React.useCallback(async () => {
		cancel();
	}, [cancel]);

	// Limpia el criteria de busqueda del formulario y restaura la lista completa de ventas.
	const onClear = React.useCallback(() => {
		formRef.current?.setValue('criteria', '');
		setFilteredSales(recentSales);
	}, [recentSales]);


	// Estado con las ventas visibles en la tabla; se actualiza al escribir en el buscador.
	const [filteredSales, setFilteredSales] = React.useState<RecentSaleDTO[]>(recentSales);

	// Definición unificada por columna: cabecera, contenido de la celda y comparador para el ordenado.
	// La comparten ambas tablas (Large device y Phone) para no duplicar la lógica de ordenado.
	const columnDefs: ChakraUtils.ColumnDef<RecentSaleDTO>[] = [
		{ key: 'FamilyName', header: Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_FAMILY_CLM(), render: s => s.FamilyName, compare: (a: string, b: string) => a.localeCompare(b) },
		{ key: 'SaleFormatName', header: Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_PRODUCT_CLM(), render: s => formatProduct(s.SaleFormatName, s), compare: (a: string, b: string) => a.localeCompare(b) },
		{ key: 'Quantity', header: Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_QUANTITY_CLM(), render: s => UI.Format.asQuantity(s.Quantity), compare: (a: number, b: number) => a - b },
		{ key: 'Price', header: Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_PRICE_CLM(), render: s => UI.Format.asMoney(s.Price), compare: (a: number, b: number) => a - b },
		{ key: 'CashDiscount', header: Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_CASH_DISCOUNT_CLM(Currency.SYMBOL), render: s => UI.Format.asMoney(s.CashDiscount), compare: (a: number, b: number) => a - b },
		{ key: 'DiscountRate', header: Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_DISCOUNT_RATE_CLM(), render: s => UI.Format.asPercentage(s.DiscountRate * 100), compare: (a: number, b: number) => a - b },
		{ key: 'TotalAmount', header: Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_TOTAL_AMOUNT_CLM(), render: s => UI.Format.asMoney(s.TotalAmount), compare: (a: number, b: number) => a - b },
		{ key: 'BusinessDay', header: Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_DATE_CLM(), render: s => UI.Format.asDate(s.BusinessDay), compare: (a: Date, b: Date) => new Date(a).getTime() - new Date(b).getTime() },
		{ key: 'UserName', header: Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_USER_CLM(), render: s => s.UserName, compare: (a: string, b: string) => a.localeCompare(b) }
	];

	// Estado de ordenado: columna seleccionada y dirección actual.
	// Al clickar la misma columna se alterna asc/desc; al clickar otra columna se empieza en asc.
	const [selectedColumn, setSelectedColumn] = React.useState<Nullable<ChakraUtils.ColumnDef<RecentSaleDTO>>>(null);
	const [columnSortDirection, setColumnSortDirection] = React.useState<Nullable<ChakraUtils.SortDirection>>(null);

	const onSortBy = React.useCallback((column: ChakraUtils.ColumnDef<RecentSaleDTO>) => {
		if (!column.compare)
			return;

		const currentSortDirection = selectedColumn?.key === column.key ? columnSortDirection : null;
		const nextDirection: ChakraUtils.SortDirection = currentSortDirection === 'asc' ? 'desc' : 'asc';

		setSelectedColumn(column);
		setColumnSortDirection(nextDirection);
		setFilteredSales(prev => ChakraUtils.sortRows(prev, column, nextDirection));
	}, [selectedColumn, columnSortDirection]);

	// Si cambia la lista original (por ejemplo al volver a abrir la pantalla), reiniciamos las filas visibles.
	React.useEffect(() => {
		setFilteredSales(recentSales);
	}, [recentSales]);


	// Filtra las filas de la tabla comparando el criterio con el valor formateado de cada columna.
	// Al ser una búsqueda por texto libre, se normaliza a minúsculas para que coincida
	// con lo que ve el usuario en pantalla (importes, porcentajes, fechas, texto, etc.).
	const onSearch = React.useCallback((criteria: string) => {
		const term = (criteria ?? '').trim().toLowerCase();

		if (term === '') {
			setFilteredSales(recentSales);
			return;
		}

		const result = recentSales.filter(sale => {
			const fields = [
				sale.FamilyName,
				formatProduct(sale.SaleFormatName, sale),
				UI.Format.asQuantity(sale.Quantity),
				UI.Format.asMoney(sale.Price),
				UI.Format.asMoney(sale.CashDiscount),
				UI.Format.asPercentage(sale.DiscountRate * 100),
				UI.Format.asMoney(sale.TotalAmount),
				UI.Format.asDate(sale.BusinessDay),
				sale.UserName
			];

			return fields.some(value => (value ?? '').toString().toLowerCase().includes(term));
		});

		setFilteredSales(result);
	}, [recentSales]);


	// Estado de qué columnas están visibles en la tabla Phone.
	// - `showAllColumns` = true  → se muestran todas las columnas y los checks individuales están desmarcados.
	// - `showAllColumns` = false → se muestran solo las columnas cuyo check individual está marcado.
	const [showAllColumns, setShowAllColumns] = React.useState<boolean>(false);
	const [checkedColumns, setCheckedColumns] = React.useState<Record<ColumnKey, boolean>>(() =>
		ALL_COLUMN_KEYS.reduce(
			(acc, k) => {
				if (k === 'TotalAmount' || k === 'SaleFormatName')
					acc[k] = true;
				else
					acc[k] = false;
				return acc;
			}, {} as Record<ColumnKey, boolean>)
	);

	// El CheckBoxMenu es "uncontrolled" internamente (solo lee `checked` en el primer render).
	// Cuando cambiamos su estado de forma programática (p.ej. al marcar "Todos" desmarcamos el resto),
	// incrementamos esta key para forzar el remount y que se pinten los checks acordes al estado actual.
	const [checkMenuKey, setCheckMenuKey] = React.useState<number>(0);
	const remountCheckMenu = React.useCallback(() => setCheckMenuKey(k => k + 1), []);

	// Conjunto de columnas actualmente visibles: si "Todos" está marcado se muestran todas,
	// en caso contrario solo las que tengan su check individual activo.
	const visibleColumns = React.useMemo<Set<ColumnKey>>(() => {
		if (showAllColumns)
			return new Set<ColumnKey>(ALL_COLUMN_KEYS);
		return new Set<ColumnKey>(ALL_COLUMN_KEYS.filter(k => checkedColumns[k]));
	}, [showAllColumns, checkedColumns]);

	// Handler para el check "Todos": al marcarlo, se muestran todas las columnas y se desmarcan los demás checks.
	// Al desmarcarlo, se mantiene el conjunto actual de checks individuales.
	const onAllCheckedChange = React.useCallback((checked: boolean) => {
		if (checked) {
			setShowAllColumns(true);
			setCheckedColumns(ALL_COLUMN_KEYS.reduce((acc, k) => { acc[k] = false; return acc; }, {} as Record<ColumnKey, boolean>));
			remountCheckMenu();
		} else {
			setShowAllColumns(false);
		}
	}, [remountCheckMenu]);

	// Handler para cada check de columna: si se marca uno cuando "Todos" estaba activo,
	// se desmarca "Todos" para pasar a modo "solo las columnas marcadas".
	const onColumnCheckedChange = React.useCallback((column: ColumnKey, checked: boolean) => {
		setCheckedColumns(prev => ({ ...prev, [column]: checked }));
		if (checked && showAllColumns) {
			setShowAllColumns(false);
			remountCheckMenu();
		}
	}, [showAllColumns, remountCheckMenu]);


	const checkMenuItems: CheckBoxMenuItemDef[] = [
		{ value: 'All', title: Res.Common.CHECK_BOX_MENU_ALL_OPTION_LBL(), checked: showAllColumns, onCheckedChange: onAllCheckedChange },
		{ value: 'FamilyName', title: Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_FAMILY_CLM(), checked: checkedColumns.FamilyName, onCheckedChange: (c) => onColumnCheckedChange('FamilyName', c) },
		{ value: 'SaleFormatName', title: Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_PRODUCT_CLM(), checked: checkedColumns.SaleFormatName, onCheckedChange: (c) => onColumnCheckedChange('SaleFormatName', c) },
		{ value: 'Quantity', title: Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_QUANTITY_CLM(), checked: checkedColumns.Quantity, onCheckedChange: (c) => onColumnCheckedChange('Quantity', c) },
		{ value: 'Price', title: Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_PRICE_CLM(), checked: checkedColumns.Price, onCheckedChange: (c) => onColumnCheckedChange('Price', c) },
		{ value: 'CashDiscount', title: Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_CASH_DISCOUNT_CLM(Currency.SYMBOL), checked: checkedColumns.CashDiscount, onCheckedChange: (c) => onColumnCheckedChange('CashDiscount', c) },
		{ value: 'DiscountRate', title: Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_DISCOUNT_RATE_CLM(), checked: checkedColumns.DiscountRate, onCheckedChange: (c) => onColumnCheckedChange('DiscountRate', c) },
		{ value: 'TotalAmount', title: Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_TOTAL_AMOUNT_CLM(), checked: checkedColumns.TotalAmount, onCheckedChange: (c) => onColumnCheckedChange('TotalAmount', c) },
		{ value: 'BusinessDay', title: Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_DATE_CLM(), checked: checkedColumns.BusinessDay, onCheckedChange: (c) => onColumnCheckedChange('BusinessDay', c) },
		{ value: 'UserName', title: Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_USER_CLM(), checked: checkedColumns.UserName, onCheckedChange: (c) => onColumnCheckedChange('UserName', c) }
	];


	return <PosScreen size={{ width: 1000, height: 720 }} screenId={'SHOW_CUSTOMER_RECENT_SALES'} useKeyPad={false}>
		<Header title={Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER()} image={Images.ShowRecentSalesByCustomer} onClose={cancel} />
		<Content>
			<LargeDeviceOnly>
				<VStack width={'full'} height={'full'} padding={'6'} gap={'9'} alignItems={'stretch'}>
					<HStack width={'full'} justifyContent={'space-between'}>
						<HStack width={'full'} gap={'2'} justifyContent={'start'} alignItems={'center'}>
							<Box minW={'7'} minH={'7'}>
								<SvgIcon src={Images.Profile} width={'7'} height={'7'} />
							</Box>
							<Text fontSize={'xl'} fontWeight={'semibold'}>{customerFiscalName}</Text>
						</HStack>
						<Form ref={formRef} initialValue={initialState} schema={stateSchema} style={{ minWidth: '401px' }}>
							<Input
								linkTo="criteria"
								placeholder={Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_SEARCH_PLACEHOLDER()}
								onWatch={(value) => onSearch(value ?? '')}
								startElement={
									<SvgIcon src={Images.Search} width={'4'} height={'4'} />
								}
								endElement={
									<SvgIcon src={Close} width={'4'} height={'4'} cursor={'pointer'} onClick={onClear} />
								}
								borderRadius={'4xl'}
								borderColor={'neutral.600'}
								height={'10'}
								width={'full'}
							/>
						</Form>
					</HStack>
					<ShowRecentSalesByCustomerTable
						filteredSales={filteredSales}
						notRecentSales={recentSales.length === 0}
						columnDefs={columnDefs}
						selectedColumn={selectedColumn}
						columnSortDirection={columnSortDirection}
						onSortBy={onSortBy}
					/>
				</VStack>
			</LargeDeviceOnly>
			<PhoneOnly>
				<VStack width={'full'} justifyContent={'space-between'} paddingX={'2'} paddingY={'4'} gap={'6'}>
					<HStack width={'full'} gap={'2'} justifyContent={'start'} alignItems={'center'}>
						<Box minW={'5'} minH={'5'}>
							<SvgIcon src={Images.Profile} width={'5'} height={'5'} />
						</Box>
						<Text fontSize={'lg'} fontWeight={'semibold'}>{customerFiscalName}</Text>
					</HStack>
					<HStack width={'full'} justifyContent={'space-between'}>
						<Form ref={formRef} initialValue={initialState} schema={stateSchema} style={{ minWidth: '100%' }}>
							<HStack width={'full'} gap={'2'} justifyContent={'space-between'}>
								<Box maxW={'60%'} flexGrow={1}>
									<Input
										linkTo="criteria"
										placeholder={Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_SEARCH_PLACEHOLDER()}
										onWatch={(value) => onSearch(value ?? '')}
										startElement={
											<SvgIcon src={Images.Search} width={'4'} height={'4'} />
										}
										endElement={
											<SvgIcon src={Close} width={'4'} height={'4'} cursor={'pointer'} onClick={onClear} />
										}
										borderRadius={'4xl'}
										borderColor={'neutral.600'}
										height={'10'}
										width={'full'}
									/>
								</Box>
								<Box flexGrow={1}>
									<CheckBoxMenu
										key={checkMenuKey}
										icon={<SvgIcon src={Images.ChevronDown} width={'4'} height={'4'} />}
										items={checkMenuItems}
									/>
								</Box>
							</HStack>
						</Form>
					</HStack>
					<ShowRecentSalesByCustomerPhoneTable
						filteredSales={filteredSales}
						notRecentSales={recentSales.length === 0}
						columnDefs={columnDefs}
						visibleColumns={visibleColumns}
						selectedColumn={selectedColumn}
						columnSortDirection={columnSortDirection}
						onSortBy={onSortBy}
					/>
				</VStack>
			</PhoneOnly>
		</Content>
		<Footer>
			<PhoneOnly>
				<BackActionButton action={onCancel} />
			</PhoneOnly>
		</Footer>
	</PosScreen >;
}

type SortableHeaderProps = {
	columnDefs: ChakraUtils.ColumnDef<RecentSaleDTO>[];
	selectedColumn: Nullable<ChakraUtils.ColumnDef<RecentSaleDTO>>;
	columnSortDirection: Nullable<ChakraUtils.SortDirection>;
	onSortBy: (column: ChakraUtils.ColumnDef<RecentSaleDTO>) => void;
};

function ShowRecentSalesByCustomerTable(props: { filteredSales: RecentSaleDTO[]; notRecentSales: boolean } & SortableHeaderProps) {
	const { filteredSales, notRecentSales, columnDefs, selectedColumn, columnSortDirection, onSortBy } = props;

	return <HStack width={'full'} height={'100'} alignItems={'stretch'}>
		<Table.ScrollArea w={'full'} minH={'0'} rounded={'md'} >
			<Table.Root size={'sm'} striped stickyHeader interactive={filteredSales.length > 0}>
				<Table.Header>
					<Table.Row>
						{columnDefs.map(col => (
							<Table.ColumnHeader key={col.key} onClick={() => onSortBy(col)} cursor={'pointer'}>
								<HStack gap={'0.5'} justifyContent={'flex-start'}>
									<Text fontSize={'sm'} fontWeight={'semibold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }}>{col.header}</Text>
									{selectedColumn?.key === col.key && <SvgIcon src={columnSortDirection === 'asc' ? Images.SortByAsc : Images.SortByDesc} width={'4'} height={'4'} />}
								</HStack>
							</Table.ColumnHeader>
						))}
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{(notRecentSales || filteredSales.length === 0) &&
						<Table.Row>
							<Table.Cell colSpan={columnDefs.length} textAlign={'center'}>
								<Box minH={'7'}>
									<Text fontSize={'sm'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>
										{(notRecentSales && Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_NOT_DATA_LBL()) || (filteredSales.length === 0 && Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_NOT_COLUMN_SELECTED_DATA_LBL())}
									</Text>
								</Box>
							</Table.Cell>
						</Table.Row>
					}
					{filteredSales.map((sale, index) => (
						<Table.Row key={index}>
							{columnDefs.map(col => (
								<Table.Cell key={col.key}><Text fontSize={'sm'}>{col.render(sale)}</Text></Table.Cell>
							))}
						</Table.Row>
					))}
				</Table.Body>
			</Table.Root>
		</Table.ScrollArea>
	</HStack>;
}

function ShowRecentSalesByCustomerPhoneTable(props: { filteredSales: RecentSaleDTO[]; notRecentSales: boolean; visibleColumns: Set<ColumnKey> } & SortableHeaderProps) {
	const { filteredSales, notRecentSales, columnDefs, visibleColumns, selectedColumn, columnSortDirection, onSortBy } = props;

	const visibleDefs = columnDefs.filter(c => visibleColumns.has(c.key as ColumnKey));

	return <HStack width={'full'} height={'100'} alignItems={'stretch'}>
		<Table.ScrollArea w={'full'} minH={'0'} rounded={'md'} >
			<Table.Root size={'sm'} striped stickyHeader interactive={filteredSales.length > 0}>
				<Table.Header>
					<Table.Row>
						{visibleDefs.map(col => (
							<Table.ColumnHeader key={col.key} onClick={() => onSortBy(col)} cursor={'pointer'}>
								<HStack gap={'0.5'} justifyContent={'flex-start'}>
									<Text fontSize={'sm'} fontWeight={'semibold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }}>{col.header}</Text>
									{selectedColumn?.key === col.key && <SvgIcon src={columnSortDirection === 'asc' ? Images.SortByAsc : Images.SortByDesc} width={'4'} height={'4'} />}
								</HStack>
							</Table.ColumnHeader>
						))}
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{(notRecentSales || filteredSales.length === 0 || visibleDefs.length === 0) &&
						<Table.Row>
							<Table.Cell colSpan={Math.max(visibleDefs.length, 1)} textAlign={'center'}>
								<Box minH={'7'}>
									<Text fontSize={'sm'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>
										{(notRecentSales && Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_NOT_DATA_LBL()) || ((filteredSales.length === 0 || visibleDefs.length === 0) && Res.Web.SHOW_RECENT_SALES_BY_CUSTOMER_NOT_COLUMN_SELECTED_DATA_LBL())}
									</Text>
								</Box>
							</Table.Cell>
						</Table.Row>
					}
					{visibleDefs.length > 0 && filteredSales.map((sale, index) => (
						<Table.Row key={index}>
							{visibleDefs.map(col => (
								<Table.Cell key={col.key}><Text fontSize={'sm'}>{col.render(sale)}</Text></Table.Cell>
							))}
						</Table.Row>
					))}
				</Table.Body>
			</Table.Root>
		</Table.ScrollArea>
	</HStack>;
}

declare global {
	interface CommandType {
		showRecentSalesByCustomer: (customer: BuyerCustomerDTO) => Promise<Result>
	}
}

Command.register('showRecentSalesByCustomer', Preconditions.and(Preconditions.ensure(Permission.Customer_ShowRecentSales), Preconditions.ensureIsNotLite), async (customer: BuyerCustomerDTO): Promise<Result> => {

	if (customer.Id === 0) {
		await Message.show(Res.Msg.MSG_CANNOT_SHOW_RECENT_SALES_BECAUSE_THERE_IS_NO_CUSTOMER_SELECTED());
		return Result.error();
	}

	const recentSales = await CustomerFacade.getRecentSales(customer.Id);
	return await Modal.show(ShowRecentSalesByCustomerScreen, { customerFiscalName: customer.FiscalName, recentSales: recentSales });
});